import type { AgentNodeProps } from '@/components/nodes/agent';
import type { AudioNodeProps } from '@/components/nodes/audio';
import type { CodeNodeProps } from '@/components/nodes/code';
import type { FileNodeProps } from '@/components/nodes/file';
import type { ImageNodeProps } from '@/components/nodes/image';
import type { LLMNodeProps } from '@/components/nodes/llm';
import type { MemoryNodeProps } from '@/components/nodes/memory';
import type { TextNodeProps } from '@/components/nodes/text';
import type { ToolNodeProps } from '@/components/nodes/tool';
import type { TweetNodeProps } from '@/components/nodes/tweet';
import type { Node } from '@xyflow/react';

export const getTextFromLLMNodes = (nodes: Node[]) => {
  const sourceTexts = nodes
    .filter((node) => node.type === 'llm')
    .map((node) => (node.data as LLMNodeProps['data']).text);

  const generatedTexts = nodes
    .filter((node) => node.type === 'llm' && node.data.generated)
    .map((node) => (node.data as LLMNodeProps['data']).generated?.text);

  return [...sourceTexts, ...generatedTexts].filter(Boolean) as string[];
};

export const getTextFromToolNodes = (nodes: Node[]) => {
  const toolTexts = nodes
    .filter((node) => node.type === 'tool')
    .map((node) => (node.data as ToolNodeProps['data']).text);

  const generatedTexts = nodes
    .filter((node) => node.type === 'tool' && node.data.generated)
    .map((node) => (node.data as ToolNodeProps['data']).generated?.result);

  return [...toolTexts, ...generatedTexts].filter(Boolean) as string[];
};

export const getTextFromAgentNodes = (nodes: Node[]) => {
  const toolTexts = nodes
    .filter((node) => node.type === 'agent')
    .map((node) => (node.data as AgentNodeProps['data']).text);

  const generatedTexts = nodes
    .filter((node) => node.type === 'agent' && node.data.generated)
    .map((node) => (node.data as AgentNodeProps['data']).generated?.result);

  return [...toolTexts, ...generatedTexts].filter(Boolean) as string[];
};

export const getTextFromTextNodes = (nodes: Node[]) => {
  const sourceTexts = nodes
    .filter((node) => node.type === 'text')
    .map((node) => (node.data as TextNodeProps['data']).text);

  const generatedTexts = nodes
    .filter((node) => node.type === 'text' && node.data.generated)
    .map((node) => (node.data as TextNodeProps['data']).generated?.text);

  // Include LLM nodes as well since they output text
  const llmTexts = getTextFromLLMNodes(nodes);

  // Include tool nodes as well since they output text
  const toolTexts = getTextFromToolNodes(nodes);

  // Include agent nodes as well since they output text
  const agentTexts = getTextFromAgentNodes(nodes);

  return [
    ...sourceTexts,
    ...generatedTexts,
    ...llmTexts,
    ...toolTexts,
    ...agentTexts,
  ].filter(Boolean) as string[];
};

export const getTranscriptionFromAudioNodes = (nodes: Node[]) => {
  const transcripts = nodes
    .filter((node) => node.type === 'audio')
    .map((node) => (node.data as AudioNodeProps['data']).transcript)
    .filter(Boolean) as string[];

  return transcripts;
};

export const getDescriptionsFromImageNodes = (nodes: Node[]) => {
  const descriptions = nodes
    .filter((node) => node.type === 'image')
    .map((node) => (node.data as ImageNodeProps['data']).description)
    .filter(Boolean) as string[];

  return descriptions;
};

export const getImagesFromImageNodes = (nodes: Node[]) => {
  const sourceImages = nodes
    .filter((node) => node.type === 'image')
    .map((node) => (node.data as ImageNodeProps['data']).content)
    .filter(Boolean) as { url: string; type: string }[];

  const generatedImages = nodes
    .filter((node) => node.type === 'image')
    .map((node) => (node.data as ImageNodeProps['data']).generated)
    .filter(Boolean) as { url: string; type: string }[];

  return [...sourceImages, ...generatedImages];
};

export const isValidSourceTarget = (source: Node, target: Node) => {
  if (source.type === 'video' || source.type === 'drop') {
    return false;
  }

  if (target.type === 'audio' && source.type !== 'text') {
    return false;
  }

  if (target.type === 'file') {
    return false;
  }

  return true;
};

export const getCodeFromCodeNodes = (nodes: Node[]) => {
  const sourceCodes = nodes
    .filter((node) => node.type === 'code')
    .map((node) => (node.data as CodeNodeProps['data']).content)
    .filter(Boolean) as { text: string; language: string }[];

  const generatedCodes = nodes
    .filter((node) => node.type === 'code' && node.data.generated)
    .map((node) => (node.data as CodeNodeProps['data']).generated)
    .filter(Boolean) as { text: string; language: string }[];

  return [...sourceCodes, ...generatedCodes];
};

export const getFilesFromFileNodes = (nodes: Node[]) => {
  const files = nodes
    .filter((node) => node.type === 'file')
    .map((node) => (node.data as FileNodeProps['data']).content)
    .filter(Boolean) as { url: string; type: string; name: string }[];

  return files;
};

export const getTweetContentFromTweetNodes = (nodes: Node[]) => {
  const tweets = nodes
    .filter((node) => node.type === 'tweet')
    .map((node) => (node.data as TweetNodeProps['data']).content)
    .filter(Boolean) as NonNullable<TweetNodeProps['data']['content']>[];

  const tweetContent = tweets.map(
    (tweet) => `On ${tweet.date}, ${tweet.author} tweeted: ${tweet.text}`
  );

  return tweetContent;
};

export const getMemoryNodes = (nodes: Node[]) => {
  return nodes.filter((node) => node.type === 'memory');
};

export const hasMemoryNodes = (nodes: Node[]) => {
  return getMemoryNodes(nodes).length > 0;
};

export const getContentFromMemoryNodes = (nodes: Node[]) => {
  const memoryContents = nodes
    .filter((node) => node.type === 'memory')
    .map((node) => {
      const memoryData = node.data as MemoryNodeProps['data'];
      // If memory has loaded content, use that; otherwise use manual content
      return memoryData.content;
    })
    .filter(Boolean) as string[];

  return memoryContents;
};

export const getMemoryNodeIds = (nodes: Node[]) => {
  const memoryIds = nodes
    .filter((node) => node.type === 'memory')
    .map((node) => {
      const memoryData = node.data as MemoryNodeProps['data'];
      // Return the memory ID if it exists
      return memoryData.generated?.memoryId;
    })
    .filter(Boolean) as string[];

  return memoryIds;
};
